/**
 * CMS Plugin Registry
 * Central registry for managing and executing CMS plugins
 */

class CMSPluginRegistry {
  constructor() {
    this.plugins = new Map();
  }

  /**
   * Register a plugin
   * @param {Object} plugin - Plugin configuration object
   */
  register(plugin) {
    if (!plugin.id) {
      throw new Error('Plugin must have an id');
    }
    if (!plugin.name) {
      throw new Error('Plugin must have a name');
    }
    if (!plugin.getInterface) {
      throw new Error('Plugin must implement getInterface method');
    }
    if (!plugin.execute) {
      throw new Error('Plugin must implement execute method');
    }

    this.plugins.set(plugin.id, plugin);
  }

  /**
   * Get a plugin by ID
   * @param {string} id - Plugin ID
   * @returns {Object|undefined} Plugin configuration
   */
  get(id) {
    return this.plugins.get(id);
  }

  /**
   * Get all plugins in a category
   * @param {string} category - Category name
   * @returns {Array} Array of plugins
   */
  getByCategory(category) {
    return Array.from(this.plugins.values())
      .filter(p => p.category === category);
  }

  /**
   * Get all registered plugins
   * @returns {Array} Array of all plugins
   */
  getAll() {
    return Array.from(this.plugins.values()).filter(p => !p.hidden);
  }

  /**
   * Validate plugin can run in given context
   * @param {string} pluginId - Plugin ID
   * @param {Object} context - Plugin context
   * @returns {Object} Validation result
   */
  validate(pluginId, context) {
    const plugin = this.get(pluginId);
    if (!plugin) {
      return {
        valid: false,
        errors: [{ message: `Plugin ${pluginId} not found` }]
      };
    }

    // Use plugin's validate method if provided
    if (plugin.validate) {
      return plugin.validate(context);
    }

    // Default validation based on required capabilities
    const errors = [];
    const caps = plugin.requiredCapabilities || {};

    if (caps.needsContentType && !context.contentType) {
      errors.push({ message: 'This plugin requires a content type' });
    }

    if (caps.needsSelection && (!context.selectedItems || context.selectedItems.length === 0)) {
      errors.push({ message: 'This plugin requires item selection' });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined
    };
  }

  /**
   * Execute a plugin
   * @param {string} pluginId - Plugin ID
   * @param {Object} context - Plugin context
   * @param {Object} params - Plugin parameters
   * @returns {Promise<Object>} Execution result
   */
  async run(pluginId, context, params) {
    const plugin = this.get(pluginId);
    if (!plugin) {
      throw new Error(`Plugin ${pluginId} not found`);
    }

    // Validate context
    const validation = this.validate(pluginId, context);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors?.map(e => e.message).join(', ')}`);
    }

    // Execute plugin
    try {
      const result = await plugin.execute(context, params);
      return result;
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: 0,
        errors: [{ error: error.message }]
      };
    }
  }
}

// Global registry instance
export const pluginRegistry = new CMSPluginRegistry();

// Helper function to infer type from value
const inferTypeFromValue = (value) => {
  if (value === null || value === undefined) return 'text';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (value instanceof Date) return 'date';
  if (Array.isArray(value)) return 'json';
  if (typeof value === 'object') return 'json';
  if (typeof value === 'string') {
    // Check if it's a date string
    if (!isNaN(Date.parse(value)) && value.includes('-')) return 'date';
    // Check if it's a long text (arbitrary threshold of 200 chars)
    if (value.length > 200) return 'longtext';
    return 'text';
  }
  return 'text';
};

// Helper function to create plugin context
export const createPluginContext = (selectedItems, contentType, controller, ui) => {
  // Aggregate information about all types in selected items
  const typeInfoMap = new Map();
  // Cache for inferred field types
  const inferredFieldTypes = new Map();

  selectedItems.forEach(item => {
    const typeName = item?.content?.type;
    if (!typeName) return;

    if (!typeInfoMap.has(typeName)) {
      typeInfoMap.set(typeName, {
        typeName,
        count: 0,
        fields: new Set(),
        rootFields: new Set()
      });
    }

    const typeInfo = typeInfoMap.get(typeName);
    typeInfo.count++;

    // Track which fields exist in item.content and infer types
    // Exclude system fields from content as well
    const excludedContentFields = ['type', '_id', '__v'];

    if (item.content) {
      Object.keys(item.content).forEach(key => {
        if (!excludedContentFields.includes(key)) {
          const fieldPath = `content.${key}`;
          typeInfo.fields.add(fieldPath);
          // Infer type from first non-null value
          if (!inferredFieldTypes.has(fieldPath) && item.content[key] != null) {
            inferredFieldTypes.set(fieldPath, inferTypeFromValue(item.content[key]));
          }
        }
      });
    }

    // Track root-level properties and infer types
    // Exclude system fields that should not be edited
    const excludedFields = ['_id', '__v', 'deleted', 'localization', 'createdAt', 'updatedAt', 'onEdit'];

    Object.keys(item).forEach(key => {
      if (key !== 'content' && !excludedFields.includes(key)) {
        typeInfo.rootFields.add(key);
        // Infer type from first non-null value
        if (!inferredFieldTypes.has(key) && item[key] != null) {
          inferredFieldTypes.set(key, inferTypeFromValue(item[key]));
        }
      }
    });
  });

  const multiTypeInfo = {
    types: Array.from(typeInfoMap.values()),
    hasMultipleTypes: typeInfoMap.size > 1,
    inferredFieldTypes
  };

  return {
    selectedItems,
    contentType,
    controller,
    ui,
    typeUtils: createTypeUtils(contentType, multiTypeInfo),
    multiTypeInfo
  };
};

// Helper to create type utilities
const createTypeUtils = (contentType, multiTypeInfo) => {
  return {
    getFieldDefinition(fieldName) {
      if (!contentType?.type_definition) return null;
      // Strip content. prefix if present
      const actualFieldName = fieldName.startsWith('content.')
        ? fieldName.substring(8)
        : fieldName;
      return contentType.type_definition.find(f => f.name === actualFieldName) || null;
    },

    getFieldsByType(fieldType) {
      if (!contentType?.type_definition) return [];
      return contentType.type_definition.filter(f => f.type === fieldType);
    },

    getAllFields() {
      return contentType?.type_definition || [];
    },

    // Get fields that are common across all selected items with type information
    getFieldsWithTypes() {
      if (!multiTypeInfo?.types || multiTypeInfo.types.length === 0) {
        return this.getAllFields().map(field => ({
          ...field,
          usedByTypes: contentType ? [contentType.type] : []
        }));
      }

      // Build a map of field name to types that use it
      const fieldTypeMap = new Map();

      multiTypeInfo.types.forEach(typeInfo => {
        // Add content fields
        typeInfo.fields.forEach(fieldName => {
          if (!fieldTypeMap.has(fieldName)) {
            fieldTypeMap.set(fieldName, []);
          }
          fieldTypeMap.get(fieldName).push(typeInfo.typeName);
        });

        // Add root-level fields
        typeInfo.rootFields.forEach(fieldName => {
          if (!fieldTypeMap.has(fieldName)) {
            fieldTypeMap.set(fieldName, []);
          }
          fieldTypeMap.get(fieldName).push(typeInfo.typeName);
        });
      });

      // Get field definitions and add type usage info
      const allFieldDefs = this.getAllFields();
      const fieldsWithTypes = [];

      fieldTypeMap.forEach((types, fieldName) => {
        // Check if it's a content field or root field
        const isContentField = fieldName.startsWith('content.');
        const actualFieldName = isContentField ? fieldName.substring(8) : fieldName;

        // Try to get field definition from type definition
        const fieldDef = allFieldDefs.find(f => f.name === actualFieldName);

        // If no field definition, create a stub with inferred type
        const inferredType = multiTypeInfo.inferredFieldTypes?.get(fieldName) || 'text';
        const stub = {
          name: fieldName,
          label: actualFieldName.charAt(0).toUpperCase() + actualFieldName.slice(1).replace(/([A-Z])/g, ' $1'),
          type: inferredType
        };

        fieldsWithTypes.push({
          ...(fieldDef || stub),
          name: fieldName, // Keep the full path (content.xyz or xyz)
          usedByTypes: types
        });
      });

      return fieldsWithTypes;
    },

    validateFieldValue(fieldName, value) {
      const field = this.getFieldDefinition(fieldName);
      if (!field) return false;

      // Basic type validation
      switch (field.type) {
        case 'text':
        case 'longtext':
          return typeof value === 'string';
        case 'number':
          return typeof value === 'number';
        case 'boolean':
          return typeof value === 'boolean';
        case 'date':
          return value instanceof Date || !isNaN(Date.parse(value));
        default:
          return true; // Unknown types pass validation
      }
    }
  };
};
